import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  ArrowRight, BookOpen, Check, ChevronRight, CirclePlay,
  Crown, Flame, Gift, ShieldCheck, Sparkles, Star, Trophy, Zap
} from "lucide-react";
import BonusCard from "../components/BonusCard";
import BlogCard from "../components/BlogCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const preferredOrder = [
  "1hitbet", "spinanga", "mafia", "fatpirate", "spinfin",
  "cazimbo", "spinsy", "vegasino", "quickwin", "wyns", "golazzo"
];

const countryLinks = [
  { name: "Italy", code: "IT", flag: "🇮🇹" },
  { name: "Switzerland", code: "CH", flag: "🇨🇭" },
  { name: "Norway", code: "NO", flag: "🇳🇴" },
  { name: "Netherlands", code: "NL", flag: "🇳🇱" },
  { name: "All Countries", code: "", flag: "🌐" },
];

function rankCasino(casino) {
  const identity = `${casino?.slug || ""} ${casino?.name || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
  const index = preferredOrder.findIndex((name) => identity.includes(name.replace(/[^a-z0-9]/g, "")));
  return index < 0 ? 999 : index;
}

function getCountry(casino) {
  return casino?.country || casino?.country_code || casino?.countryCode || casino?.geo || casino?.country_name || "";
}

function getBonus(casino) {
  return casino?.bonus_title || casino?.bonus_amount || casino?.bonus_description ||
    (casino?.free_spins ? `${casino.free_spins} Free Spins` : "") || "View current offer";
}

function safeRating(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n.toFixed(1) : "—";
}

function CasinoShowcaseCard({ casino, index }) {
  const name = casino?.name || "Online Casino";
  const country = getCountry(casino);
  const rating = safeRating(casino?.rating);
  const bonus = getBonus(casino);
  const logo = casino?.logo;
  const external = casino?.website_url;

  return (
    <article className={`ps-casino-card ${index === 0 ? "ps-casino-card-featured" : ""}`} data-testid={`home-featured-${casino?.slug || index}`}>
      <div className="ps-card-topline">
        <span className={`ps-rank ps-rank-${index + 1}`}>{index + 1}</span>
        <div className="ps-casino-brand">
          {logo ? (
            <img src={logo} alt={`${name} logo`} loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }} />
          ) : (
            <span className="ps-logo-fallback">{name.slice(0, 2).toUpperCase()}</span>
          )}
        </div>
        <div className="ps-rating"><Star size={13} fill="currentColor" /> {rating}</div>
      </div>
      <h3 className="ps-casino-name">{name}</h3>
      <div className="ps-country-tag">{country ? country.toString().toUpperCase() : "CASINO REVIEW"}</div>
      <div className="ps-bonus-box">{bonus}</div>
      <ul className="ps-feature-list">
        <li><Check size={14} /> Bonus terms and conditions</li>
        <li><Check size={14} /> Casino details and review</li>
        <li><Check size={14} /> Check availability in your region</li>
      </ul>
      {external ? (
        <a className="ps-visit-button" href={external} target="_blank" rel="nofollow sponsored noopener noreferrer">
          Visit Site <ArrowRight size={15} />
        </a>
      ) : (
        <Link className="ps-visit-button" to={`/casinos/${casino?.slug || ""}`}>
          View Casino <ArrowRight size={15} />
        </Link>
      )}
      <Link className="ps-review-link" to={`/casinos/${casino?.slug || ""}`}>Read Review</Link>
    </article>
  );
}

export default function Home() {
  const [casinos, setCasinos] = useState([]);
  const [trendingCasinos, setTrendingCasinos] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      try {
        const [casinosRes, bonusesRes, blogsRes] = await Promise.all([
          axios.get(`${API}/casinos`),
          axios.get(`${API}/bonuses`),
          axios.get(`${API}/blogs`),
        ]);
        if (!active) return;
        const casinoData = Array.isArray(casinosRes.data) ? casinosRes.data : [];
        setCasinos(casinoData);
        setTrendingCasinos(casinoData.filter((casino) => casino.is_trending));
        setBonuses(Array.isArray(bonusesRes.data) ? bonusesRes.data : []);
        setBlogs(Array.isArray(blogsRes.data) ? blogsRes.data : []);
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => { active = false; };
  }, []);

  const featuredCasinos = useMemo(() => {
    return [...casinos].sort((a, b) => {
      const rankDifference = rankCasino(a) - rankCasino(b);
      if (rankDifference !== 0) return rankDifference;
      if (Boolean(a?.is_featured) !== Boolean(b?.is_featured)) return a?.is_featured ? -1 : 1;
      return (Number(b?.rating) || 0) - (Number(a?.rating) || 0);
    }).slice(0, 5);
  }, [casinos]);

  return (
    <main className="ps-home" data-testid="home-page">
      <Helmet>
        <title>PlaySpiro – Best Online Casino Reviews, Slots &amp; Bonuses 2026</title>
        <meta name="description" content="Compare online casino reviews, bonus details, free spins and game guides in one place. Check offer terms and availability before you play." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://playspiro.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://playspiro.com/" />
        <meta property="og:title" content="PlaySpiro – Best Online Casino Reviews, Slots &amp; Bonuses 2026" />
        <meta property="og:description" content="Trusted casino reviews, bonus details and game guides. Compare offers with PlaySpiro." />
        <meta property="og:image" content="https://playspiro.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "PlaySpiro",
          url: "https://playspiro.com",
          description: "Online casino reviews, bonuses and game guides",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://playspiro.com/casinos?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}</script>
      </Helmet>

      <section className="ps-hero">
        <div className="ps-hero-glow ps-hero-glow-one" aria-hidden="true" />
        <div className="ps-hero-glow ps-hero-glow-two" aria-hidden="true" />
        <div className="ps-hero-stars" aria-hidden="true">✦　·　✧　　✦　·　✧　✦</div>
        <div className="ps-shell ps-hero-layout">
          <div className="ps-hero-copy">
            <div className="ps-eyebrow"><Sparkles size={14} /> Trusted online casino reviews</div>
            <h1>Find the Best<br />Online Casinos<br />for <span>Real Players</span></h1>
            <p className="ps-hero-intro">Honest reviews, bonus information and casino comparisons — all in one place. Play smarter with PlaySpiro.</p>
            <div className="ps-hero-actions">
              <Link to="/casinos" className="ps-button ps-button-primary"><Star size={17} fill="currentColor" /> View Top Casinos <ArrowRight size={17} /></Link>
              <Link to="/about" className="ps-button ps-button-outline"><CirclePlay size={18} /> How We Review</Link>
            </div>
            <div className="ps-trust-points">
              <div><ShieldCheck /><span><b>Trusted Reviews</b><small>Clear and transparent</small></span></div>
              <div><Gift /><span><b>Bonus Information</b><small>Terms made clearer</small></span></div>
              <div><Zap /><span><b>Player Focus</b><small>Compare before joining</small></span></div>
            </div>
            <p className="ps-disclaimer">18+ only. Gambling involves risk. Offers vary by country. Always check the operator's terms and play responsibly.</p>
          </div>

          <div className="ps-hero-art" aria-label="Casino-themed illustration">
            <div className="ps-art-halo" />
            <div className="ps-coin ps-coin-one">€</div>
            <div className="ps-coin ps-coin-two">✦</div>
            <div className="ps-casino-chip ps-chip-one">♠</div>
            <div className="ps-casino-chip ps-chip-two">♦</div>
            <div className="ps-slot-machine">
              <div className="ps-slot-top"><Crown size={24} /> <span>LUCKY SPIN</span></div>
              <div className="ps-slot-window"><span>7</span><span>7</span><span>7</span></div>
              <div className="ps-slot-base"><span>JACKPOT</span><i /></div>
            </div>
            <div className="ps-award-card"><Trophy size={22} /><strong>PLAY SMARTER</strong><span>Compare before you play</span></div>
            <div className="ps-jackpot-card">
              <div className="ps-jackpot-title"><Trophy size={16} /> LIVE JACKPOTS</div>
              {[
                ["🎰", "Mega Moolah", "€12,458,931"],
                ["💎", "Divine Fortune", "€2,384,112"],
                ["👑", "Major Millions", "€1,205,331"],
                ["🎲", "Jackpot 6000", "€985,421"],
                ["🍀", "Hall of Gods", "€723,662"],
              ].map(([icon, name, amount]) => (
                <div className="ps-jackpot-row" key={name}><span>{icon}</span><span>{name}</span><b>{amount}</b></div>
              ))}
              <small className="ps-jackpot-note">Illustrative values — not live operator data</small>
            </div>
          </div>
        </div>
      </section>

      <div className="ps-trust-strip">
        <div className="ps-shell ps-trust-strip-inner"><span>COMPARE WITH CONFIDENCE</span><b>NO LIMIT CITY</b><b>PRAGMATIC PLAY</b><b>Evolution</b><b>PLAY’n GO</b><b>BGAMING</b><b>HACKSAW</b></div>
      </div>

      <section className="ps-section ps-top-casinos">
        <div className="ps-shell">
          <div className="ps-section-heading">
            <div><h2><Crown size={28} /> Top Online Casinos</h2><p>Compare bonuses, features and reviews to find an option that suits you.</p></div>
            <Link to="/casinos" className="ps-button ps-button-primary ps-button-small">View All Casinos <ArrowRight size={16} /></Link>
          </div>
          {loading ? (
            <div className="ps-casino-grid">{[0,1,2,3,4].map((item) => <div className="ps-casino-skeleton" key={item} />)}</div>
          ) : featuredCasinos.length ? (
            <div className="ps-casino-grid">{featuredCasinos.map((casino, index) => <CasinoShowcaseCard key={casino.id || casino.slug || index} casino={casino} index={index} />)}</div>
          ) : (
            <div className="ps-empty-state">Casino listings are temporarily unavailable. Please try again soon.</div>
          )}
          <div className="ps-country-grid">
            {countryLinks.map((country) => (
              <Link key={country.name} to="/casinos" className="ps-country-card">
                <span className="ps-country-flag">{country.flag}</span><span><b>{country.name}</b><small>{country.code ? `Casino offers · ${country.code}` : "Browse all listings"}</small></span><ChevronRight size={18} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="ps-feature-panels">
        <div className="ps-shell ps-feature-grid">
          <article className="ps-feature-panel ps-bonus-panel"><div className="ps-panel-icon"><Gift size={25} /></div><div><h3>Latest Casino Bonuses</h3><p>Explore welcome offers and free spins. Always review the conditions.</p></div><Link to="/bonuses" className="ps-button ps-button-outline">View Bonuses <ArrowRight size={15} /></Link></article>
          <article className="ps-feature-panel ps-trending-panel"><div className="ps-panel-icon"><Flame size={25} /></div><div><h3>Trending Casinos</h3><p>See casino listings marked as trending.</p></div>{trendingCasinos.slice(0, 3).map((casino, index) => <Link className="ps-trending-row" to={`/casinos/${casino.slug}`} key={casino.id || casino.slug || index}><span className="ps-trending-rank">{index + 1}</span><span>{casino.name}</span><span className="ps-trending-rating"><Star size={12} fill="currentColor" /> {safeRating(casino.rating)}</span><ChevronRight size={15} /></Link>)}{!trendingCasinos.length && <Link className="ps-inline-link" to="/casinos">Explore casino listings <ArrowRight size={14} /></Link>}</article>
          <article className="ps-feature-panel ps-guide-panel"><div className="ps-panel-icon"><BookOpen size={25} /></div><div><h3>Latest Guides &amp; Reviews</h3><p>Read practical guides to casino features, offers and safer play.</p></div><Link to="/blog" className="ps-button ps-button-outline">Read Guides <ArrowRight size={15} /></Link></article>
        </div>
      </section>

      {bonuses.length > 0 && <section className="ps-section ps-secondary-section"><div className="ps-shell"><div className="ps-section-heading"><div><h2><Gift size={25} /> Latest Casino Bonuses</h2><p>Review offer details and conditions before claiming a promotion.</p></div><Link to="/bonuses" className="ps-inline-link">All bonuses <ArrowRight size={15} /></Link></div><div className="ps-secondary-grid">{bonuses.slice(0, 4).map((bonus) => <BonusCard key={bonus.id} bonus={bonus} />)}</div></div></section>}

      {blogs.length > 0 && <section className="ps-section ps-secondary-section"><div className="ps-shell"><div className="ps-section-heading"><div><h2><BookOpen size={25} /> Latest Guides &amp; Reviews</h2><p>Explore casino guides and editorial updates from PlaySpiro.</p></div><Link to="/blog" className="ps-inline-link">View all guides <ArrowRight size={15} /></Link></div><div className="ps-blog-grid">{blogs.slice(0, 3).map((blog) => <BlogCard key={blog.id || blog.slug} blog={blog} />)}</div></div></section>}

      <section className="ps-responsible"><div className="ps-shell"><ShieldCheck size={25} /><div><h2>Make informed choices</h2><p>Check licensing, eligibility, wagering requirements and withdrawal rules. Gambling should be entertainment, not a way to make money.</p></div><Link to="/about" className="ps-inline-link">About PlaySpiro <ArrowRight size={15} /></Link></div></section>
    </main>
  );
}
